/**
 * Champ 6 email popup.
 * Signups are tagged "popup-signup" on the customer, so they can be counted in Shopify.
 * Views and signups are also published as customer events (c6_email_popup_viewed /
 * c6_email_popup_submitted) so a custom pixel can count them for the signup rate.
 */
(() => {
  if (customElements.get('c6-email-popup')) return;

  // Every page opens at the top. Stops the browser restoring an old scroll spot, and undoes
  // any jump that happens while the page loads, until the shopper scrolls or taps themselves.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  if (!location.hash || location.hash.includes('C6PopForm')) {
    let userMoved = false;
    const markMoved = () => { userMoved = true; };
    for (const type of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
      addEventListener(type, markMoved, { once: true, passive: true, capture: true });
    }
    const keepTop = () => { if (!userMoved && scrollY > 0) window.scrollTo(0, 0); };
    addEventListener('scroll', keepTop, { passive: true });
    keepTop();
    addEventListener('load', () => {
      keepTop();
      setTimeout(() => removeEventListener('scroll', keepTop), 1500);
    }, { once: true });
  }

  const KEY_SNOOZE = 'c6_popup_snooze_until';
  const KEY_DONE = 'c6_popup_subscribed';
  const store = {
    get(key) {
      try { return localStorage.getItem(key); } catch { return null; }
    },
    set(key, value) {
      try { localStorage.setItem(key, value); } catch {}
    },
  };

  const track = (name, data = {}) => {
    const payload = { ...data, page: location.pathname };
    try { window.Shopify?.analytics?.publish?.(name, payload); } catch {}
    try { (window.dataLayer = window.dataLayer || []).push({ event: name, ...payload }); } catch {}
  };

  class C6EmailPopup extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('.c6-pop__card');
      this.teaser = this.querySelector('[data-teaser]');
      this.form = this.querySelector('[data-c6-form]');
      this.opened = false;

      this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelector('[data-yes]')?.addEventListener('click', () => {
        track('c6_email_popup_yes');
        this.#step('form');
      });
      this.teaser?.addEventListener('click', () => this.open('teaser'));
      this.form?.addEventListener('submit', (event) => this.#submit(event));
      this.querySelector('[data-copy]')?.addEventListener('click', () => this.#copy());
      this.querySelector('[data-close-link]')?.addEventListener('click', () => this.close(true));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      // Shopify's redirect after a non-JS signup adds #C6PopForm, which would jump the page
      // down to the footer. Drop the hash and keep the shopper at the top.
      const cameFromPopup = location.hash.includes('C6PopForm');
      if (cameFromPopup) {
        history.replaceState(history.state, '', location.pathname + location.search);
        window.scrollTo(0, 0);
        window.addEventListener('load', () => window.scrollTo(0, 0), { once: true });
      }
      if (location.search.includes('customer_posted=true') && cameFromPopup) {
        store.set(KEY_DONE, '1');
        this.open('posted');
        this.#step('done');
        return;
      }

      if (this.dataset.designMode === 'true') {
        if (this.dataset.preview === 'true') this.open('editor');
        return;
      }

      if (store.get(KEY_DONE)) return;
      const snoozeUntil = Number(store.get(KEY_SNOOZE) || 0);
      if (Date.now() < snoozeUntil) {
        this.#showTeaser();
        return;
      }
      this.#arm();
    }

    #arm() {
      const fire = (reason) => {
        if (this.opened) return;
        cleanup();
        this.open(reason);
      };
      const delay = Number(this.dataset.delay) || 0;
      // 0 seconds turns the timer off, so only the scroll trigger opens it
      const timer = delay > 0 ? setTimeout(() => fire('timer'), delay * 1000) : 0;

      const scrollTarget = Number(this.dataset.scroll) || 0;
      const onScroll = () => {
        const max = document.documentElement.scrollHeight - innerHeight;
        if (scrollTarget && max > 0 && (scrollY / max) * 100 >= scrollTarget) fire('scroll');
      };
      const onLeave = (event) => {
        if (event.clientY <= 0 && !event.relatedTarget) fire('exit');
      };

      if (scrollTarget) addEventListener('scroll', onScroll, { passive: true });
      const desktop = matchMedia('(pointer: fine)').matches;
      if (this.dataset.exit === 'true' && desktop) document.addEventListener('mouseout', onLeave);

      const cleanup = () => {
        clearTimeout(timer);
        removeEventListener('scroll', onScroll);
        document.removeEventListener('mouseout', onLeave);
      };
    }

    open(reason) {
      this.opened = true;
      this.classList.remove('is-teaser-only');
      this.classList.add('is-open');
      if (this.teaser) this.teaser.hidden = true;
      this.lastFocus = document.activeElement;
      this.card?.focus({ preventScroll: true });
      document.documentElement.style.overflow = 'hidden';
      if (reason !== 'editor' && reason !== 'posted') track('c6_email_popup_viewed', { trigger: reason });
    }

    close(subscribed = false) {
      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (this.dataset.designMode === 'true') return;
      if (!subscribed && !store.get(KEY_DONE)) {
        const days = Number(this.dataset.snoozeDays) || 7;
        store.set(KEY_SNOOZE, String(Date.now() + days * 864e5));
        track('c6_email_popup_closed');
        this.#showTeaser();
      }
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    #showTeaser() {
      if (!this.teaser || store.get(KEY_DONE)) return;
      this.classList.add('is-teaser-only');
      this.teaser.hidden = false;
    }

    #step(name) {
      this.querySelectorAll('[data-step]').forEach((el) => (el.hidden = el.dataset.step !== name));
      if (name === 'form') setTimeout(() => this.querySelector('input[type="email"]')?.focus({ preventScroll: true }), 50);
    }

    async #submit(event) {
      event.preventDefault();
      const button = this.form.querySelector('[data-submit]');
      const error = this.form.querySelector('[data-error]');
      const email = this.form.querySelector('input[type="email"]');
      if (!email?.value || !email.checkValidity()) {
        if (error) error.hidden = false;
        return;
      }
      button.disabled = true;
      if (error) error.hidden = true;

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'text/html' },
        });
        // Shopify sends bot checks to /challenge: let the browser handle that page
        if (response.url.includes('/challenge')) {
          store.set(KEY_DONE, '1');
          track('c6_email_popup_submitted', { via: 'challenge' });
          HTMLFormElement.prototype.submit.call(this.form);
          return;
        }
        if (!response.ok || !response.url.includes('customer_posted=true')) throw new Error('signup failed');

        store.set(KEY_DONE, '1');
        track('c6_email_popup_submitted');
        this.#applyCode();
        this.#step('done');
      } catch (err) {
        console.error('[Champ6] popup signup failed', err);
        if (error) error.hidden = false;
        button.disabled = false;
      }
    }

    #applyCode() {
      const code = this.dataset.code;
      if (!code) return;
      fetch(`${window.Shopify?.routes?.root || '/'}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {});
    }

    async #copy() {
      const code = this.dataset.code;
      const label = this.querySelector('[data-copy-label]');
      try {
        await navigator.clipboard.writeText(code);
        if (label) label.textContent = 'Copied ✓';
      } catch {
        if (label) label.textContent = code;
      }
    }
  }

  customElements.define('c6-email-popup', C6EmailPopup);
})();
